import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { UseLoginReturnProps } from '@/features/auth/types/login.type';
import { useSpinner } from '@chikara/ui';
import { t } from '@/i18n'
import * as ROUTES from '@/shared/constants/routes'
import { mfaService } from '@/features/auth/services/mfaService';
import { tokenStorage } from '@/features/auth/utils/tokenStorage';

/** Segmento del path `/api/v1/commands/<slug>` usado como `command` en MFA (p. ej. login). */
const MFA_LOGIN_COMMAND = 'login';
const MFA_LOGIN_TYPE = 'login';

export function useLogin(): UseLoginReturnProps {
  const { login: authLogin, user, loginWithEmail: firebaseLogin } = useAuth();
  const { show, hide } = useSpinner();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || ROUTES.DASHBOARD;

  const loginWithEmail = useCallback(
    async (email: string, password: string) => {
      show();
      setError(null);

      try {
        const result = await firebaseLogin(email, password);
        setError(null);

        if (result?.mfa_enabled) {
          // Evita que un `setup_token` residual del onboarding haga caer el OTP en `mfa-validate` con mfa-setup
          tokenStorage.clearSetupToken();

          const { data: sendData } = await mfaService.mfaSend({
            command: MFA_LOGIN_COMMAND,
            type: MFA_LOGIN_TYPE,
          });

          tokenStorage.setMfaChallengeContext({
            command: sendData.command,
            type: sendData.type,
            mfa_token: sendData.mfa_token,
          });
        }

        return navigate(ROUTES.MFA, { state: { mfaState: result?.mfa_enabled } });

      } catch (err) {
        setError(err instanceof Error ? err.message : t('login.errorLogin'));
      } finally {
        hide();
      }
    },
    [authLogin, navigate, from, user]
  );

  const login = useCallback(
    async () => {
      show();
      setError(null);

      try {
        await authLogin();
        navigate(from, { replace: true });
        return;
      } catch (err) {
        setError(err instanceof Error ? err.message : t('login.errorLogin'));
      } finally {
        hide();
      }
    },
    [authLogin, navigate, from, user]
  );

  const completeMfa = () => {
    return login();
  };

  return { loginWithEmail, login, completeMfa, error };
}
